import { Collapse, IconButton, makeStyles, Paper, Theme } from '@material-ui/core';
import { Styles } from '@material-ui/core/styles/withStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import classnames from 'classnames';
import React from 'react';
import { colors, ColorTypeEnum } from 'theme';

type CollapsableCardStyleProps = {
  color?: ColorTypeEnum;
  disableCollapse?: boolean;
  maxWidth?: string;
  disableBorder?: boolean;
};

const collapsableCardStyles: Styles<Theme, CollapsableCardStyleProps> = theme => ({
  collapsableCard: {
    margin: 'auto',
    marginBottom: '10px',
    maxWidth: ({ maxWidth }) => maxWidth ?? '',
  },
  cardWrapper: {
    width: '100%',
    height: '55px',
    borderLeft: ({ disableBorder, color }) =>
      `${disableBorder ? '0px' : '9px'} solid ${color ? theme.palette[color].main : theme.palette.primary.main}`,
    borderRadius: '0px',
    cursor: ({ disableCollapse }) => (disableCollapse ? 'auto' : 'pointer'),
  },
  card: {
    display: 'flex',
    height: '100%',
    padding: '0 20px',
  },
  cardContent: {
    width: '90%',
    display: 'flex',
    alignItems: 'center',

    '& > p': {
      fontWeight: '600',
    },
  },
  collapsedContent: {
    width: '100%',
    minHeight: '5vh',
    padding: '2vh',
    backgroundColor: colors.greyLightest,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  icon: {
    border: 'solid 1px',
    borderRadius: '50%',
    color: theme.palette.text.primary,
  },
});

const useStyles = makeStyles(collapsableCardStyles);

type CollapsableCardProps = CollapsableCardStyleProps & {
  collapsableContent?: React.ReactNode;
  disableCollapse?: boolean;
  maxWidth?: string;
  defaultExpanded?: boolean;
  actionClick?: boolean;
  dataQaId?: string;
};

const CollapsableCard: React.FC<CollapsableCardProps> = ({
  collapsableContent,
  disableCollapse,
  children,
  color,
  maxWidth,
  defaultExpanded,
  disableBorder,
  dataQaId,
  actionClick,
}) => {
  const classes = useStyles({ disableCollapse, color, maxWidth, disableBorder });
  const [expanded, setExpanded] = React.useState(defaultExpanded ?? false);

  const handleExpandClick = (event: any) => {
    if (actionClick) {
      return;
    }

    let classNameElement = event.target.getAttribute('class');
    if (null === classNameElement) {
      classNameElement = event.target.parentNode.getAttribute('class');
    }

    if (/MuiButton|userAction/.test(classNameElement)) {
      return;
    }

    if (!disableCollapse) {
      setExpanded(!expanded);
    }
  };

  return (
    <div className={classes.collapsableCard}>
      <Paper className={classes.cardWrapper} onClick={handleExpandClick}>
        <div className={classes.card}>
          <div className={classes.cardContent}>{children}</div>
          {!disableCollapse && (
            <IconButton
              className={classnames(classes.expand, {
                [classes.expandOpen]: expanded,
              })}
              aria-expanded={expanded}
              aria-label="show more"
              data-qa-id={dataQaId ? dataQaId : ''}
            >
              <ExpandMoreIcon className={classes.icon} />
            </IconButton>
          )}
        </div>
      </Paper>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.collapsedContent}>{collapsableContent}</div>
      </Collapse>
    </div>
  );
};

CollapsableCard.defaultProps = {
  color: ColorTypeEnum.PRIMARY,
};

export default CollapsableCard;
